// Client-side filtering/sorting/view switching over server-rendered event cards.
// Root: [data-event-filter] containing template-parts/filter-sidebar.php, event-controls.php and event-grid.php.
// Cards carry data-date (Y-m-d), data-title, data-price, data-cats, data-venue, data-text (see CONVENTIONS.md).
import { qs, qsa, on } from './dom.js';

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const addDays = (ymd, n) => { const d = new Date(ymd + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const isDesktop = () => window.matchMedia('(min-width: 768px)').matches;
const isYmd = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));
/** Saturday–Sunday of the current weekend (today when it is Sat/Sun). */
export function weekendRange(ymd) {
  const day = new Date(ymd + 'T00:00:00').getDay();
  if (day === 0) return [ymd, ymd];
  const sat = addDays(ymd, 6 - day);
  return [sat, addDays(sat, 1)];
}
const PERIODS = ['today', 'weekend', 'next2weeks'];
const SORTS = ['date_asc', 'date_desc', 'name_asc', 'name_desc'];
const VIEW_STORAGE_KEY = 'kt_events_view';

const getSavedView = () => {
  try {
    const v = sessionStorage.getItem(VIEW_STORAGE_KEY);
    return (v === 'list' || v === 'grid') ? v : null;
  } catch {
    return null;
  }
};

const saveView = (view) => {
  try {
    if (view === 'list' || view === 'grid') {
      sessionStorage.setItem(VIEW_STORAGE_KEY, view);
    }
  } catch {}
};

// Restore list view early on DOMContentLoaded / load to prevent layout flicker
function applyEarlyView() {
  if (getSavedView() === 'list' && isDesktop()) {
    document.querySelectorAll('[data-event-list]').forEach((el) => el.classList.add('view-list'));
    document.querySelectorAll('[data-filter-view]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.filterView === 'list'));
    });
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyEarlyView, { once: true });
} else {
  applyEarlyView();
}

function setup(root) {
  const grid = qs('[data-event-grid]', root);
  const list = qs('[data-event-list]', root);
  if (!grid || !list) return;
  const perPage = Number(grid.dataset.perPage || 12);
  const cards = qsa('[data-event-card]', list);
  const sidebar = qs('[data-filter-sidebar]', root);
  const backdrop = qs('[data-filter-backdrop]', root);
  const controls = qs('[data-filter-controls]', root);
  const search = qs('[data-filter="search"]', root);
  const from = qs('[data-filter="from"]', root);
  const to = qs('[data-filter="to"]', root);
  const sort = qs('[data-filter-sort]', root);
  const empty = qs('[data-filter-empty]', root);
  const moreWrap = qs('[data-load-more-wrap]', root);
  const countEls = qsa('[data-filter-count]', root);
  const resetPills = qsa('[data-filter-controls] [data-filter-reset]', root);

  const savedView = getSavedView();
  const initialView = savedView || (isDesktop() ? 'grid' : 'list');
  const state = { period: 'all', limit: perPage, view: initialView, open: isDesktop() };
  if (initialView === 'list' && isDesktop()) {
    list.classList.add('view-list');
    qsa('[data-filter-view]', root).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.filterView === 'list')));
  }

  const cats = () => qsa('[data-filter="cat"]:checked', root).map((i) => i.value);
  const venues = () => qsa('[data-filter="venue"]:checked', root).map((i) => i.value);

  function activeCount() {
    let n = 0;
    if (state.period !== 'all') n++;
    if (search && search.value.trim()) n++;
    if (cats().length) n++;
    if (venues().length) n++;
    if (from && from.value && state.period === 'all') n++;
    return n;
  }

  function matches(card) {
    const q = (search?.value || '').trim().toLowerCase();
    if (q && !(card.dataset.text || '').includes(q)) return false;
    const c = cats();
    if (c.length && !(card.dataset.cats || '').split(' ').some((s) => c.includes(s))) return false;
    const v = venues();
    if (v.length && !v.includes(card.dataset.venue)) return false;
    const date = card.dataset.date || '';
    const today = todayYmd();
    if (date && date < today) return false;
    if (state.period === 'today' && date !== today) return false;
    if (state.period === 'weekend') { const [a, b] = weekendRange(today); if (!(date >= a && date <= b)) return false; }
    if (state.period === 'next2weeks' && !(date >= today && date <= addDays(today, 14))) return false;
    if (from?.value && date < from.value) return false;
    if (to?.value && date > to.value) return false;
    return true;
  }

  function apply() {
    const order = sort?.value || 'date_asc';
    const sorted = [...cards].sort((a, b) => {
      switch (order) {
        case 'date_desc': return (b.dataset.date || '').localeCompare(a.dataset.date || '') || (a.dataset.title || '').localeCompare(b.dataset.title || '');
        case 'name_asc': return (a.dataset.title || '').localeCompare(b.dataset.title || '', undefined, { sensitivity: 'base' });
        case 'name_desc': return (b.dataset.title || '').localeCompare(a.dataset.title || '', undefined, { sensitivity: 'base' });
        default: return (a.dataset.date || '').localeCompare(b.dataset.date || '') || (a.dataset.title || '').localeCompare(b.dataset.title || '');
      }
    });
    let shown = 0;
    let total = 0;
    sorted.forEach((card) => {
      const ok = matches(card);
      if (ok) total++;
      const visible = ok && shown < state.limit;
      if (visible) shown++;
      card.hidden = !visible;
      list.appendChild(card); // reorder in place
    });
    if (empty) empty.hidden = total > 0;
    if (moreWrap) moreWrap.hidden = shown >= total;
    const n = activeCount();
    countEls.forEach((el) => { el.textContent = String(n); });
    resetPills.forEach((p) => { p.hidden = n === 0; });
    qsa('[data-filter-period]', root).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.filterPeriod === state.period)));
    writeUrl();
  }

  // URL <-> filter state (only on roots with data-url-sync, i.e. /events/): ?q=&when=today|weekend|next2weeks&from=&to=
  // (&date= = from+to)&cat=slug,slug&venue=id,id&sort=&view=list&open=filters – shareable, and the start page quick
  // filter bar deep-links here with these params.
  const sync = root.hasAttribute('data-url-sync');
  function readUrl() {
    if (!sync) return;
    const p = new URLSearchParams(window.location.search);
    if (search && p.get('q')) search.value = p.get('q').slice(0, 100);
    const csv = (k) => (p.get(k) || '').split(',').map((x) => x.trim()).filter(Boolean);
    const cs = csv('cat');
    qsa('[data-filter="cat"]', root).forEach((i) => { i.checked = cs.includes(i.value); });
    const vs = csv('venue');
    qsa('[data-filter="venue"]', root).forEach((i) => { i.checked = vs.includes(i.value); });
    const when = p.get('when');
    if (PERIODS.includes(when)) {
      setPeriod(when, true);
    } else {
      const d = p.get('date');
      if (from && isYmd(d)) { from.value = d; if (to) to.value = d; }
      if (from && isYmd(p.get('from'))) from.value = p.get('from');
      if (to && isYmd(p.get('to'))) to.value = p.get('to');
    }
    if (sort && SORTS.includes(p.get('sort'))) sort.value = p.get('sort');
    if (p.get('view') === 'list' || p.get('view') === 'grid') {
      state.view = p.get('view');
      saveView(state.view);
    }
    if (p.get('open') === 'filters') state.open = true;
  }
  function writeUrl() {
    if (!sync || !window.history || !window.history.replaceState) return;
    const p = new URLSearchParams();
    const q = (search?.value || '').trim();
    if (q) p.set('q', q);
    if (state.period !== 'all') p.set('when', state.period);
    else {
      if (from?.value) p.set('from', from.value);
      if (to?.value) p.set('to', to.value);
    }
    if (cats().length) p.set('cat', cats().join(','));
    if (venues().length) p.set('venue', venues().join(','));
    if (sort && sort.value && sort.value !== 'date_asc') p.set('sort', sort.value);
    const qs2 = p.toString().replace(/%2C/g, ',');
    const url = window.location.pathname + (qs2 ? '?' + qs2 : '') + window.location.hash;
    if (url !== window.location.pathname + window.location.search + window.location.hash) window.history.replaceState(null, '', url);
  }

  function setPeriod(val, silent = false) {
    if ((state.period === val && !silent) || val === 'all') {
      state.period = 'all';
      if (from) from.value = '';
      if (to) to.value = '';
    } else {
      state.period = val;
      const today = todayYmd();
      if (val === 'today') { from.value = today; to.value = today; }
      else if (val === 'weekend') { [from.value, to.value] = weekendRange(today); }
      else if (val === 'next2weeks') { from.value = today; to.value = addDays(today, 14); }
      else { from.value = ''; to.value = ''; }
    }
    state.limit = perPage;
    if (!silent) apply();
  }

  function reset() {
    if (search) search.value = '';
    qsa('[data-filter="cat"], [data-filter="venue"]', root).forEach((i) => { i.checked = false; });
    state.period = 'all';
    if (from) from.value = '';
    if (to) to.value = '';
    state.limit = perPage;
    apply();
  }

  function setOpen(open) {
    state.open = open;
    if (!sidebar) return;
    const openCls = ['translate-x-0', 'md:w-80', 'md:opacity-100', 'md:mr-0'];
    const closedCls = ['-translate-x-full', 'md:w-0', 'md:opacity-0', 'md:overflow-hidden', 'md:-mr-4'];
    sidebar.classList.remove(...(open ? closedCls : openCls));
    sidebar.classList.add(...(open ? openCls : closedCls));
    if (backdrop) backdrop.hidden = !open;
    if (controls) controls.classList.toggle('md:static', open);
    qsa('[data-filter-toggle]', root).forEach((b) => b.setAttribute('aria-expanded', String(open)));
    if (open && !isDesktop()) document.body.classList.add('overflow-hidden');
    else document.body.classList.remove('overflow-hidden');
  }

  function setView(view, persist = true) {
    state.view = view;
    list.classList.toggle('view-list', view === 'list');
    qsa('[data-filter-view]', root).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.filterView === view)));
    if (persist && (view === 'list' || view === 'grid')) {
      saveView(view);
    }
  }

  // events
  // analytics (1.9.0): which filter was used (once per kind and page view; no-op without the tracker)
  const used = {};
  const track = (kind) => { if (!used[kind]) { used[kind] = 1; window.ktTrack?.('filter', kind); } };
  on(root, 'input', '[data-filter="search"]', () => { state.limit = perPage; apply(); track('text'); });
  on(root, 'change', '[data-filter="cat"], [data-filter="venue"]', (e, el) => { state.limit = perPage; apply(); track(el.dataset.filter === 'cat' ? 'genre' : 'venue'); });
  on(root, 'change', '[data-filter="from"], [data-filter="to"]', () => { if (state.period !== 'all') state.period = 'all'; state.limit = perPage; apply(); track('date'); });
  on(root, 'change', '[data-filter-sort]', () => { apply(); track('sort'); });
  on(root, 'click', '[data-filter-period]', (e, el) => { setPeriod(el.dataset.filterPeriod); track('when'); });
  on(root, 'click', '[data-filter-reset]', (e) => { e.preventDefault(); reset(); });
  on(root, 'click', '[data-filter-toggle]', () => setOpen(!state.open));
  on(root, 'click', '[data-filter-close], [data-filter-backdrop]', () => setOpen(false));
  on(root, 'click', '[data-filter-view]', (e, el) => setView(el.dataset.filterView, true));
  on(root, 'click', '[data-load-more]', () => { state.limit += perPage; apply(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && state.open && !isDesktop()) setOpen(false); });
  let wasDesktop = isDesktop();
  window.addEventListener('resize', () => {
    const d = isDesktop();
    if (d !== wasDesktop) { wasDesktop = d; setOpen(d); setView(d ? state.view : 'list', false); }
  });
  window.addEventListener('pageshow', () => {
    const v = getSavedView();
    if (v && isDesktop() && v !== state.view) {
      setView(v, false);
    }
  });

  readUrl();
  setOpen(state.open);
  setView(isDesktop() ? state.view : 'list', false);
  apply();
}

export function initEventFilter() {
  qsa('[data-event-filter]').forEach(setup);
}
